import { useEffect, useRef, useState } from "react";
import Layout from "@/components/Layout";
import { useUser } from "@/context/UserContext";
import api from "@/utils/api";
import { Building2, FileText, MapPin, MessageCircle, Save, Upload, User } from "lucide-react";
import { PushToggle } from "@/components/PushNotificationBanner";
import ChangePasswordSection from "@/components/ChangePasswordSection";
import DeleteAccountSection from "@/components/DeleteAccountSection";

export default function ClientProfileV2() {
  const { user, updateUser } = useUser();
  const [form, setForm] = useState({
    name: "",
    phone: "",
    organization: "",
    billing_address: "",
    preferred_communication: "E-Mail",
    saved_locations: [],
  });
  const [newLocation, setNewLocation] = useState("");
  const [costApprovals, setCostApprovals] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get(`/client-profiles/${user.id}`).then((r) => {
      setForm({
        name: r.data.name || "",
        phone: r.data.phone || "",
        organization: r.data.organization || "",
        billing_address: r.data.billing_address || "",
        preferred_communication: r.data.preferred_communication || "E-Mail",
        saved_locations: r.data.saved_locations || [],
      });
    }).catch(() => {});
    api.get(`/cost-approvals/${user.id}`).then((r) => setCostApprovals(r.data || [])).catch(() => {});
  }, [user.id]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/client-profiles/${user.id}`, form);
      updateUser({ name: form.name });
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } finally {
      setSaving(false);
    }
  };

  const addLocation = () => {
    const value = newLocation.trim();
    if (!value || form.saved_locations.includes(value)) return;
    setForm({ ...form, saved_locations: [...form.saved_locations, value] });
    setNewLocation("");
  };

  const uploadCostApproval = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const body = new FormData();
    body.append("file", file);
    try {
      const res = await api.post(`/cost-approvals/${user.id}`, body, { headers: { "Content-Type": "multipart/form-data" } });
      setCostApprovals((prev) => [...prev, res.data]);
    } catch {
      alert("Upload fehlgeschlagen.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Mein Profil</h1>

        <form onSubmit={save} className="space-y-6">
          <section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2"><User size={20} className="text-[#CA8A04]" /> Persönliche Daten</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <label className="text-sm font-medium text-gray-700">Name
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 w-full px-4 py-3 border border-gray-200 rounded-xl" />
              </label>
              <label className="text-sm font-medium text-gray-700">Telefon (optional)
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 w-full px-4 py-3 border border-gray-200 rounded-xl" placeholder="+49 ..." />
              </label>
              <label className="text-sm font-medium text-gray-700 sm:col-span-2">E-Mail
                <input value={user.email} disabled className="mt-1 w-full px-4 py-3 border border-gray-100 rounded-xl bg-gray-50 text-gray-400" />
              </label>
            </div>
          </section>

          <section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2"><Building2 size={20} className="text-[#CA8A04]" /> Organisation & Rechnung</h2>
            <div className="space-y-4">
              <label className="block text-sm font-medium text-gray-700">Organisation / Firma
                <input value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} className="mt-1 w-full px-4 py-3 border border-gray-200 rounded-xl" placeholder="z. B. Schule, Klinik, Unternehmen" />
              </label>
              <label className="block text-sm font-medium text-gray-700">Rechnungsadresse
                <textarea rows={3} value={form.billing_address} onChange={(e) => setForm({ ...form, billing_address: e.target.value })} className="mt-1 w-full px-4 py-3 border border-gray-200 rounded-xl resize-none" />
              </label>
            </div>
          </section>

          <section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2"><MessageCircle size={20} className="text-[#CA8A04]" /> Kommunikation</h2>
            <select value={form.preferred_communication} onChange={(e) => setForm({ ...form, preferred_communication: e.target.value })} className="w-full px-4 py-3 border border-gray-200 rounded-xl">
              <option>E-Mail</option><option>Chat</option><option>SMS</option><option>Telefon</option>
            </select>
          </section>

          <section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2"><MapPin size={20} className="text-[#CA8A04]" /> Gespeicherte Einsatzorte</h2>
            <div className="flex gap-2">
              <input value={newLocation} onChange={(e) => setNewLocation(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addLocation(); } }} className="flex-1 px-4 py-3 border border-gray-200 rounded-xl" placeholder="Adresse hinzufügen" />
              <button type="button" onClick={addLocation} className="px-4 py-3 rounded-xl bg-gray-100 font-medium">Hinzufügen</button>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {form.saved_locations.map((loc) => <button type="button" key={loc} onClick={() => setForm({ ...form, saved_locations: form.saved_locations.filter((x) => x !== loc) })} className="px-3 py-1.5 rounded-full bg-gray-100 text-sm text-gray-600">{loc} ×</button>)}
            </div>
          </section>

          <div className="flex justify-end"><button disabled={saving} className="px-6 py-3 bg-[#14B8A6] text-gray-900 font-semibold rounded-xl flex items-center gap-2"><Save size={18} /> {saved ? "Gespeichert" : saving ? "Speichern…" : "Speichern"}</button></div>
        </form>

        <section className="mt-6 bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2"><FileText size={20} className="text-[#CA8A04]" /> Kostenzusagen</h2>
          <p className="text-sm text-gray-500 mb-4">Kostenzusagen können direkt an eine Anfrage angehängt werden.</p>
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="flex items-center gap-2 px-5 py-3 border-2 border-dashed border-[#14B8A6] text-[#0D9488] rounded-xl hover:bg-[#F0FDFA] transition-colors text-sm font-medium">
            <Upload size={18} /> {uploading ? "Wird hochgeladen…" : "Kostenzusage hochladen"}
          </button>
          <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={uploadCostApproval} className="hidden" />
          <div className="mt-4 space-y-2">
            {costApprovals.length === 0 ? <p className="text-sm text-gray-400">Keine Kostenzusagen hochgeladen.</p> : costApprovals.map((item) => (
              <div key={item.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-200">
                <FileText size={18} className="text-[#0D9488] shrink-0" />
                <div className="flex-1 min-w-0"><p className="text-sm font-medium text-gray-900 truncate">{item.filename}</p><p className="text-xs text-gray-400">{item.uploaded_at ? new Date(item.uploaded_at).toLocaleDateString("de-DE") : ""}</p></div>
                <a href={`${process.env.REACT_APP_BACKEND_URL}${item.url}`} target="_blank" rel="noreferrer" className="text-xs text-[#0D9488] hover:underline">Öffnen</a>
              </div>
            ))}
          </div>
        </section>

        <div className="mt-6"><PushToggle /></div>
        <div className="mt-6 space-y-6"><ChangePasswordSection /><DeleteAccountSection /></div>
      </div>
    </Layout>
  );
}
